<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <VariableSelect
      v-model="statementPayload.payload.variable"
      label="Assign to Variable"
      :filter="lhsVariableFilter"
      class="flex-grow"
      :placeholder="$t('evaluate.select_variable')"
      @update:modelValue="onVariableInput"
      @option:selected="onVariableOptionSelected"
    >
    </VariableSelect>
    <div class="flex">
      <div class="mt-3 flex-grow">
        <label class="text-gray-600 text-12 mb-1">{{ $t('function') }}</label>
        <v-select
          v-if="functionList && functionList.length"
          :options="functionList"
          searchable
          :modelValue="statementPayload.payload.value.function"
          appendToBody
          @update:modelValue="onFunctionSelected"
        >
          <template v-slot:option="{ label, description, returns }">
            <div class="flex items-center">
              <div>
                <div>{{ label }}</div>
                <div class="text-11">{{ description }}</div>
              </div>
              <Badge small class="ml-auto" :text="returns" info></Badge>
            </div>
          </template>
        </v-select>
        <div v-else class="flex items-center text-13 min-h-9">{{ $t('flow.no_match_functions', { dataType: variableDataType }) }}</div>
      </div>
    </div>

    <div v-if="selectedFunction" class="mt-3">
      <label class="block text-12 text-gray-600 mb-1 mt-3">{{ $t('collections.parameters') }}</label>
      <div v-for="(argument, index) in selectedFunction.arguments" :key="argument.name + index" class="mb-2">
        <label class="block text-12 text-gray-600 mb-1 mt-3">
          {{ argument.name }} <span class="text-12 bg-gray-200 rounded-6 px-1">&lt;{{ argument.type }}&gt;</span>
          <Icon name="info" class="w-4 h-4 inline-block -mb-1 ml-1" :title="argument.description" />
        </label>
        <div class="w-full flex gap-x-3">
          <template v-if="argument.type === 'Text'">
            <FormSelect
              placeholder="Select Data Type"
              class="w-1/2"
              autoSelect
              v-model="statementPayload.payload.value.argumentsData[index].dataType"
              default-reducer
              :options="getParameterDataTypes(index)"
              :clearable="false"
              @option:selected="onParameterDataTypeInput(index)"
            ></FormSelect>
            <FormSelect
              v-if="isUserDataType(statementPayload.payload.value.argumentsData[index].dataType)"
              class="w-1/2"
              key="static"
              :clearable="false"
              :modelValue="statementPayload.payload.value.arguments[index]"
              default-reducer
              :options="getSelectableParameters(statementPayload.payload.value.argumentsData[index].dataType)"
              @option:selected="onFunctionParameterSelect($event, index)"
            ></FormSelect>
            <PhraseEditor
              v-else
              v-model="statementPayload.payload.value.arguments[index]"
              @update:modelValue="updateStatementValue($event, index, argument)"
              class="w-1/2"
              :filter-data-type="[statementPayload.payload.value.argumentsData[index].dataType]"
            />
          </template>

          <VariableSelect
            v-else
            class="w-1/2"
            curly
            :filter-data-type="getParameterDataTypes(index, true)"
            v-model="statementPayload.payload.value.arguments[index]"
            @update:modelValue="updateStatementValue($event, index, argument)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from '../../../helpers/debounce';
import EvaluateItemsConfig from './EvaluateItemsConfig';

export default {
  name: 'DataAnalysisConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      statementPayload: structuredClone(EvaluateItemsConfig['data-analysis'].payload),
      selectedFunction: null,
    };
  },
  computed: {
    ...mapState(['dataAnalysisFunctions', 'projectData']),
    projectId() {
      return this.$route.params.projectId;
    },
    variables() {
      return this.projectData?.[this.projectId]?.variables || {};
    },
    entities() {
      return this.projectData?.[this.projectId]?.datatypes || {};
    },
    functionList() {
      if (this.dataAnalysisFunctions) {
        let functions = Object.entries(this.dataAnalysisFunctions)
          .map((fn) => {
            const [key, value] = fn;
            return {
              label: key,
              value: key,
              ...value,
            };
          })
          .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        if (this.statementPayload.payload.variable) {
          functions = functions.filter((fn) => {
            const returns = fn.returns.split('|');
            return returns.includes(this.variables[this.statementPayload.payload.variable]?.dataType);
          });
        }
        return functions;
      }
      return [];
    },
    variableDataType() {
      return this.variables?.[this.statementPayload.payload.variable]?.dataType;
    },
    getDataTypes() {
      let dataTypes = [];
      if (this.variables?.[this.statementPayload.payload.variable]?.dataType) {
        if (this.variables?.[this.statementPayload.payload.variable]?.dataType === 'Text') {
          dataTypes = Object.keys(this.entities)
            .filter((dataType) => !this.entities[dataType].tags.includes('system'))
            .filter((dataType) => !['DataTable', 'Chart'].includes(dataType));

          if (
            this.statementPayload.payload.variable &&
            this.variables?.[this.statementPayload.payload.variable]?.dataType &&
            !dataTypes.includes(this.variables?.[this.statementPayload.payload.variable]?.dataType)
          ) {
            dataTypes.push(this.variables[this.statementPayload.payload.variable].dataType);
          }
        } else {
          dataTypes.push(this.variables[this.statementPayload.payload.variable].dataType);
        }
      }

      return dataTypes.map((dataType) => ({ label: dataType, value: dataType })).sort((a, b) => a.label - b.label);
    },
  },
  methods: {
    onVariableOptionSelected() {
      this.emitPayload();
    },
    onVariableInput(e) {
      if (e === null) {
        this.clearRHS();
        this.emitPayload();
      }
    },
    onFunctionParameterSelect(field, index) {
      this.statementPayload.payload.value.arguments[index] = field.value;
      this.emitPayload();
    },
    isUserDataType(dataType) {
      return typeof this.entities[dataType]?.fields === 'object';
    },
    onParameterDataTypeInput(index) {
      this.statementPayload.payload.value.arguments[index] = null;
    },
    getParameterDataTypes(index, rawArray) {
      let dataTypes = [];
      if (this.selectedFunction) {
        if (this.selectedFunction.arguments[index].type.split('|').includes('Text')) {
          dataTypes = Object.keys(this.entities)
            .filter((dataType) => !this.entities[dataType].tags.includes('system'))
            .filter((dataType) => !['DataTable', 'Chart'].includes(dataType));
        }
        const argTypes = this.selectedFunction.arguments[index].type.split('|');
        if (this.statementPayload.payload.value.argumentsData[index].type === 'static') {
          argTypes.forEach((argType) => {
            if (!dataTypes.includes(argType)) dataTypes.push(argType);
          });
        } else {
          argTypes.forEach((argType) => {
            if (!dataTypes.includes(argType)) dataTypes.push(argType);
          });
        }
      }
      if (rawArray) {
        return dataTypes;
      }
      return dataTypes.map((dataType) => ({ label: dataType, value: dataType })).sort((a, b) => a.label - b.label);
    },
    getSelectableParameters(dataType) {
      if (dataType) {
        const fields = this.entities[dataType].fields || {};
        return Object.keys(fields).map((field) => ({ label: field, value: field }));
      }
      return [];
    },
    lhsVariableFilter(variable) {
      if (this.statementPayload.payload.value && this.statementPayload.payload.assignment_type === 'variable') {
        return this.variables[this.statementPayload.payload.value].dataType === variable.dataType && !variable.readonly;
      }
      if (this.statementPayload.payload.assignment_type === 'function' && this.selectedFunction) {
        const returns = this.selectedFunction.returns.split('|');
        return returns.includes(variable.dataType) && !variable.readonly;
      }
      return ['DataTable', 'Text', 'Number'].includes(variable.dataType) && !variable.readonly;
    },
    clearRHS() {
      this.statementPayload.payload.value = {
        function: null,
        arguments: [],
        argumentsData: [],
      };
      this.selectedFunction = null;

      if (this.getDataTypes.length === 1) {
        this.statementPayload.payload.dataType = this.getDataTypes[0].value;
      } else {
        this.statementPayload.payload.dataType = null;
      }
    },
    updateStatementValue(value, index) {
      this.statementPayload.payload.value.arguments[index] = value;
      this.emitPayload();
    },
    onFunctionSelected(e) {
      this.selectedFunction = e;

      this.statementPayload.payload.value.argumentsData = [];
      if (this.selectedFunction?.arguments) {
        this.selectedFunction.arguments.forEach((args, index) => {
          this.statementPayload.payload.value.argumentsData[index] = { dataType: null, type: 'variable' };
        });

        // eslint-disable-next-line
        const args = Array.apply(null, Array(this.selectedFunction.arguments.length)).map(() => null);
        this.statementPayload.payload.value.arguments = args;
        this.statementPayload.payload.dataType = null;
      }

      this.statementPayload.payload.value.function = e?.label || null;
    },
    // eslint-disable-next-line
    debouncedPayload: debounce(function () {
      const statementPayload = JSON.parse(JSON.stringify(this.statementPayload));

      if (statementPayload.payload.assignment_type === 'function') {
        delete statementPayload.payload.value.argumentsData;
      }
      delete statementPayload.payload.dataType;

      this.$emit('update', statementPayload);
    }, 300),
    emitPayload(e) {
      if (e) {
        if (this.selectedFunction) {
          const returns = this.selectedFunction.returns.split('|');
          if (!returns.includes(this.variables[e].dataType)) {
            this.clearRHS();
          }
        }
      }

      this.debouncedPayload();
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.statementPayload = JSON.parse(JSON.stringify(this.item));
          // Set function
          const fn = this.functionList.find((f) => f.title === this.statementPayload.payload.value.function);
          this.selectedFunction = fn;
          this.statementPayload.payload.value.function = fn?.title || null;

          this.statementPayload.payload.value.argumentsData = [];
          this.selectedFunction?.arguments?.forEach((args, index) => {
            this.statementPayload.payload.value.argumentsData[index] = { dataType: null, type: 'variable' };
            const value = this.statementPayload.payload.value.arguments[index];
            if (value) {
              if (value.toString().startsWith('{') && value.toString().endsWith('}')) {
                const variableName = value.toString().substring(1).slice(0, -1);
                this.statementPayload.payload.value.argumentsData[index].type = 'variable';
                this.statementPayload.payload.value.argumentsData[index].dataType = this.variables?.[variableName]?.dataType || null;
              } else {
                this.statementPayload.payload.value.argumentsData[index].type = 'static';

                this.statementPayload.payload.value.argumentsData[index].dataType = args.type;
              }
            }
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
